import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import learningPortalImage from "../assets/images/learningportal.svg";
import Error from "../components/ui/Error";
import { useLoginMutation } from "../features/auth/authApi";
import { useFormik } from "formik";
import { Helmet } from 'react-helmet';
import * as Yup from 'yup';
import { ROLE_TYPE, URLPath } from "../utils/constantVariables";


const validationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email address').required('Required'),
  password: Yup.string().required('Required'),
});

export default function Login() {
  //Variables
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [login, { isLoading }] = useLoginMutation();
  const isAdminPage = pathname === URLPath.ADMIN;
  const initialValues = { email: '', password: '' };

  //States
  const [error, setError] = useState('');

  //Functions
  const onSubmit = async (values, { setSubmitting }) => {
    if (values.email === 'admin@learnwithsumit.com' && pathname === URLPath.ROOT) {
      setError("Authentication Error!");
      return;
    }
    setError('');
    setSubmitting(true);

    try {
      const { data, error } = await login(values);
      if (data?.accessToken && data?.user?.role === ROLE_TYPE.ADMIN) {
        navigate(URLPath.DASHBOARD);
      } else if (data?.accessToken && data?.user?.role === ROLE_TYPE.STUDENT) {
        navigate(URLPath.COURSE_PLAYER);
      } else if (error?.data) {
        setError(error.data);
      }
    } catch (error) {
      if (error?.response?.data) {
        setError(error.response.data);
      } else {
        setError('Something went wrong!');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const { handleSubmit, touched, errors, getFieldProps } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });



  return (
    <>
      <Helmet>
        <title>{`${isAdminPage ? 'Admin' : 'Student'} Login`}</title>
      </Helmet>
      <section className="py-6 bg-primary h-screen grid place-items-center">
        <div className="mx-auto max-w-md px-5 lg:px-0">
          <div>
            <img
              className="h-12 mx-auto"
              src={learningPortalImage}
              alt="logo"
            />
            <h2 className="mt-6 text-center text-3xl font-extrabold text-slate-100">
              {`Sign in to ${isAdminPage ? 'Admin' : 'Student'} Account`}
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="email-address" className="sr-only">
                  Email address
                </label>
                <input
                  id="email-address"
                  type="email"
                  autoComplete="email"
                  className="login-input rounded-t-md"
                  placeholder="Email address"
                  {...getFieldProps('email')}
                />
                {touched.email && errors.email && (
                  <div className="text-red-600">{errors.email}</div>
                )}
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  className="login-input rounded-b-md text-light"
                  placeholder="Password"
                  required
                  {...getFieldProps('password')}
                />
                {touched.password && errors.password && (
                  <div className="text-red-600">{errors.password}</div>
                )}
              </div>
            </div>
            {!isAdminPage && (
              <div className="flex items-center justify-end">
                <div className="text-sm">
                  <Link
                    to="/register"
                    className="font-medium text-violet-600 hover:text-violet-500"
                  >
                    Create New Account
                  </Link>
                </div>
              </div>
            )}
            <div>
              <button
                type="submit"
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                disabled={isLoading}
              >
                Sign in
              </button>
            </div>
          </form>
          {error !== "" && <Error message={error} />}
        </div>
      </section>
    </>

  );
}
